(() => {
  'use strict';
  const instances = new Map();

  function mount(sequence) {
    if (instances.has(sequence)) return;
    const viewport = sequence.querySelector('.hero-sequence__viewport');
    const intro = sequence.querySelector('.supply-hero');
    const photo = sequence.querySelector('.hero');
    const header = document.querySelector('.header-viewport');
    if (!viewport || !intro || !photo) return;

    const lifecycle = new AbortController();
    const signal = lifecycle.signal;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const clamp = value => Math.max(0, Math.min(1, value));
    let enabled = false;
    let start = 0;
    let travel = 1;
    let frame = 0;
    let measureFrame = 0;
    let sizes;
    let visibility;

    function render() {
      frame = 0;
      if (!enabled) return;
      const progress = clamp((window.scrollY - start) / travel);
      sequence.style.setProperty('--hero-progress', progress.toFixed(4));
      sequence.style.setProperty('--intro-opacity', (1 - clamp(progress / .55)).toFixed(4));
      sequence.style.setProperty('--photo-opacity', clamp((progress - .04) / .7).toFixed(4));
      sequence.style.setProperty('--copy-opacity', clamp((progress - .42) / .44).toFixed(4));
      sequence.dataset.scene = progress >= .75 ? 'photo' : 'intro';
    }

    function requestRender() {
      if (!frame && enabled) frame = window.requestAnimationFrame(render);
    }

    function measure() {
      measureFrame = 0;
      if (!enabled) return;
      const navHeight = header?.getBoundingClientRect().height || 0;
      sequence.style.setProperty('--nav-height', `${navHeight}px`);
      const sceneHeight = viewport.offsetHeight;
      const sceneTop = Math.min(navHeight, window.innerHeight - sceneHeight);
      travel = Math.max(620, window.innerHeight * 1.05);
      sequence.style.setProperty('--scene-top', `${sceneTop}px`);
      sequence.style.setProperty('--sequence-height', `${sceneHeight + travel}px`);
      start = sequence.getBoundingClientRect().top + window.scrollY - sceneTop;
      render();
    }

    function requestMeasure() {
      if (!measureFrame && enabled) measureFrame = window.requestAnimationFrame(measure);
    }

    function configure() {
      enabled = !motion.matches;
      if (enabled) {
        sequence.classList.add('is-motion');
        measure();
      } else {
        sequence.classList.remove('is-motion');
        sequence.removeAttribute('data-scene');
        sequence.removeAttribute('style');
      }
    }

    sequence.addEventListener('focusin', event => {
      if (!enabled || !(event.target instanceof Element) || !event.target.matches(':focus-visible')) return;
      const progress = clamp((window.scrollY - start) / travel);
      if (photo.contains(event.target) && progress < 1) {
        window.scrollTo({ top: start + travel, behavior: 'instant' });
        render();
      } else if (intro.contains(event.target) && progress > 0) {
        window.scrollTo({ top: start, behavior: 'instant' });
        render();
      }
    }, { signal });

    window.addEventListener('scroll', requestRender, { passive: true, signal });
    window.addEventListener('resize', requestMeasure, { passive: true, signal });
    window.addEventListener('pageshow', requestMeasure, { signal });
    motion.addEventListener('change', configure, { signal });
    configure();
    if ('ResizeObserver' in window) {
      sizes = new ResizeObserver(requestMeasure);
      sizes.observe(viewport);
      if (header) sizes.observe(header);
    }
    if (typeof IntersectionObserver !== 'undefined') {
      visibility = new IntersectionObserver(entries => {
        sequence.dataset.visible = String(entries.some(entry => entry.isIntersecting));
      });
      visibility.observe(sequence);
    }
    document.fonts?.ready.then(() => {
      if (!signal.aborted) requestMeasure();
    });

    instances.set(sequence, () => {
      lifecycle.abort();
      enabled = false;
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(measureFrame);
      sizes?.disconnect();
      visibility?.disconnect();
      sequence.classList.remove('is-motion');
      sequence.removeAttribute('data-scene');
      sequence.removeAttribute('data-visible');
      sequence.removeAttribute('style');
    });
  }

  function mountAll(root) {
    if (root.matches?.('[data-hero-sequence]')) mount(root);
    root.querySelectorAll('[data-hero-sequence]').forEach(mount);
  }

  function unmountAll(root) {
    for (const [sequence, cleanup] of instances) {
      if (root === sequence || root.contains(sequence)) {
        cleanup();
        instances.delete(sequence);
      }
    }
  }

  document.addEventListener('shopify:section:load', event => {
    unmountAll(event.target);
    mountAll(event.target);
  });
  document.addEventListener('shopify:section:unload', event => unmountAll(event.target));
  mountAll(document);
})();
